import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/AuthContext";
import { formatCurrency, formatDate } from "@/lib/format";
import { ArrowLeft, CreditCard, FileText, AlertTriangle, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TIERS = ["pro", "growth", "agency"];

export default function Billing() {
  const { user, setUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmPlan, setConfirmPlan] = useState(null);
  const loc = i18n.language?.startsWith("pt") ? "pt" : "en";
  const en = loc === "en";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/billing");
      setData(r.data);
    } catch {
      setData({ plan: user?.plan || "free", subscription: null, payment_method: null, invoices: [] });
    }
  }, [user]);

  useEffect(() => {
    const setupSession = params.get("setup_session");
    (async () => {
      if (setupSession) {
        try {
          await api.post("/billing/payment-method/confirm", { session_id: setupSession });
          toast.success(t("billing.cardSaved"));
        } catch { /* falls back to webhook */ }
        params.delete("setup_session");
        setParams(params, { replace: true });
      }
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sub = data?.subscription;
  const plan = data?.plan || "free";
  const currency = (sub?.currency || (en ? "usd" : "brl")).toUpperCase();
  const yearly = sub?.interval === "year";

  const action = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
      await load();
      try {
        const me = await api.get("/auth/me");
        setUser?.(me.data);
      } catch { /* keep current user */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.genericError"));
    } finally {
      setBusy("");
    }
  };

  const cancel = () => action("cancel", async () => {
    await api.post("/billing/cancel");
    toast.success(t("billing.canceledToast"));
  });

  const resume = () => action("resume", async () => {
    await api.post("/billing/resume");
    toast.success(t("billing.resumedToast"));
  });

  const changePlan = (tier) => action("plan", async () => {
    const key = `${tier}_${yearly ? "yearly" : "monthly"}${(sub?.currency || "") === "usd" ? "_usd" : ""}`;
    await api.post("/billing/change-plan", { lookup_key: key });
    toast.success(t("billing.planChanged", { plan: t(`pricing.${tier}Name`) }));
  });

  const addCard = () => action("card", async () => {
    const r = await api.post("/billing/payment-method", { origin_url: window.location.origin });
    window.location.href = r.data.checkout_url;
  });

  const statusLabel = () => {
    if (!sub) return t("billing.statusFree");
    if (sub.cancel_at_period_end) return t("billing.statusCanceling", { date: formatDate(sub.current_period_end, loc) });
    if (sub.status === "trialing") return t("billing.statusTrial", { date: formatDate(sub.trial_end || sub.current_period_end, loc) });
    if (sub.status === "active") return t("billing.statusActive", { date: formatDate(sub.current_period_end, loc) });
    if (sub.status === "past_due" || sub.status === "unpaid") return t("billing.statusPastDue");
    return sub.status;
  };

  const needsCard = sub && !data?.payment_method && ["trialing", "past_due", "unpaid"].includes(sub.status);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" data-testid="billing-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900 transition-colors">
            <ArrowLeft size={16} /> {t("campaignDetail.back")}
          </Link>
          <span className="font-heading font-extrabold text-lg tracking-tight">depoia<span className="text-[#FF4F00]">.</span></span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="billing-page">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight">{t("billing.title")}</h1>
        <p className="text-sm text-zinc-500 mt-1">{t("billing.sub")}</p>

        {!data ? (
          <div className="mt-10 flex items-center gap-2 text-sm text-zinc-500"><Loader2 size={16} className="animate-spin" /> {t("common.loading")}</div>
        ) : (
          <>
            <section className="mt-8 bg-white border border-zinc-200 rounded-lg p-6" data-testid="current-plan-card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">{t("billing.currentPlan")}</p>
                  <p className="font-heading font-extrabold text-2xl mt-1" data-testid="current-plan-name">
                    {plan === "free" ? t("pricing.freeName") : t(`pricing.${plan}Name`)}
                  </p>
                  {sub && (
                    <p className="text-sm text-zinc-600 mt-1" data-testid="current-plan-price">
                      {formatCurrency((sub.amount || 0) / 100, currency, loc)}
                      <span className="text-zinc-400">/{yearly ? (en ? "yr" : "ano") : (en ? "mo" : "mês")}</span>
                    </p>
                  )}
                  <p className="text-sm text-zinc-500 mt-2" data-testid="billing-status">{statusLabel()}</p>
                </div>
                {plan === "free" && (
                  <button
                    data-testid="billing-see-plans"
                    onClick={() => navigate(`/${loc}/pricing`)}
                    className="px-5 py-2.5 bg-[#FF4F00] text-white rounded-full text-sm font-semibold hover:bg-[#E64600] transition-colors"
                  >
                    {t("pricing.startTrial")}
                  </button>
                )}
              </div>

              {needsCard && (
                <div className="mt-5 flex flex-wrap items-center gap-3 bg-orange-50 border border-orange-200 rounded-md p-4" data-testid="needs-card-warning">
                  <AlertTriangle size={18} className="text-[#FF4F00] shrink-0" />
                  <p className="text-sm text-zinc-700 flex-1">{t("billing.needsCard")}</p>
                  <button
                    data-testid="add-card-btn"
                    onClick={addCard}
                    disabled={!!busy}
                    className="px-4 py-2 bg-[#0A0A0A] text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-50"
                  >
                    {busy === "card" ? t("common.loading") : t("billing.addCard")}
                  </button>
                </div>
              )}

              {sub && (
                <div className="mt-5 pt-5 border-t border-zinc-100 flex flex-wrap items-center gap-3">
                  {data.payment_method ? (
                    <span className="inline-flex items-center gap-2 text-sm text-zinc-600" data-testid="payment-method">
                      <CreditCard size={16} className="text-zinc-400" />
                      {data.payment_method.brand?.toUpperCase()} •••• {data.payment_method.last4}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-sm text-zinc-500" data-testid="payment-method">
                      <CreditCard size={16} className="text-zinc-400" /> {t("billing.noCard")}
                    </span>
                  )}
                  <button
                    data-testid="change-card-btn"
                    onClick={addCard}
                    disabled={!!busy}
                    className="text-sm font-semibold text-zinc-700 underline underline-offset-4 hover:text-[#FF4F00] transition-colors disabled:opacity-50"
                  >
                    {data.payment_method ? t("billing.changeCard") : t("billing.addCard")}
                  </button>
                  <span className="flex-1" />
                  {sub.cancel_at_period_end ? (
                    <button
                      data-testid="resume-btn"
                      onClick={resume}
                      disabled={!!busy}
                      className="px-5 py-2.5 bg-[#0A0A0A] text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-50"
                    >
                      {busy === "resume" ? t("common.loading") : t("billing.resume")}
                    </button>
                  ) : (
                    <button
                      data-testid="cancel-btn"
                      onClick={() => setConfirmCancel(true)}
                      disabled={!!busy}
                      className="text-sm font-semibold text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
                    >
                      {t("billing.cancel")}
                    </button>
                  )}
                </div>
              )}
            </section>

            {sub && (
              <section className="mt-8" data-testid="change-plan-section">
                <h2 className="font-heading font-bold text-lg">{t("billing.changePlanTitle")}</h2>
                <p className="text-sm text-zinc-500 mt-1">{t("billing.changePlanDesc")}</p>
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                  {TIERS.map((tier) => {
                    const current = plan === tier;
                    return (
                      <div key={tier} className={`rounded-lg p-5 border ${current ? "border-[#FF4F00] bg-white" : "border-zinc-200 bg-white"}`} data-testid={`switch-${tier}-card`}>
                        <p className="font-heading font-bold">{t(`pricing.${tier}Name`)}</p>
                        <p className="text-xs text-zinc-500 mt-1 min-h-[32px]">{t(`pricing.${tier}Desc`)}</p>
                        {current ? (
                          <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#FF4F00]" data-testid={`switch-${tier}-current`}>
                            <Check size={15} /> {t("pricing.current")}
                          </div>
                        ) : (
                          <button
                            data-testid={`switch-${tier}-btn`}
                            onClick={() => setConfirmPlan(tier)}
                            disabled={!!busy}
                            className="mt-4 w-full py-2.5 border border-zinc-300 rounded-full text-sm font-semibold hover:border-zinc-900 transition-colors disabled:opacity-50"
                          >
                            {t("billing.switchTo")}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {data.invoices?.length > 0 && (
              <section className="mt-10" data-testid="invoices-section">
                <h2 className="font-heading font-bold text-lg">{t("billing.invoices")}</h2>
                <div className="mt-4 bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
                  {data.invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between gap-4 px-5 py-3.5" data-testid={`invoice-row-${inv.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">{formatDate(inv.created, loc)}</p>
                        <p className="text-xs text-zinc-500">{t(`billing.invStatus.${inv.status}`, inv.status)} · {formatCurrency((inv.amount || 0) / 100, (inv.currency || currency).toUpperCase(), loc)}</p>
                      </div>
                      {(inv.pdf_url || inv.hosted_url) && (
                        <a
                          href={inv.pdf_url || inv.hosted_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-700 hover:text-[#FF4F00] transition-colors"
                        >
                          <FileText size={15} /> {t("billing.receipt")}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("billing.cancelConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("billing.cancelConfirmDesc", { date: formatDate(sub?.current_period_end, loc) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-dismiss">{t("billing.keepPlan")}</AlertDialogCancel>
            <AlertDialogAction data-testid="cancel-dialog-confirm" onClick={cancel} className="bg-red-600 hover:bg-red-700">
              {t("billing.confirmCancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmPlan} onOpenChange={(o) => !o && setConfirmPlan(null)}>
        <AlertDialogContent data-testid="change-plan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("billing.switchConfirmTitle", { plan: confirmPlan ? t(`pricing.${confirmPlan}Name`) : "" })}</AlertDialogTitle>
            <AlertDialogDescription>{t("billing.switchConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="change-plan-dismiss">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="change-plan-confirm"
              onClick={() => { const tier = confirmPlan; setConfirmPlan(null); changePlan(tier); }}
              className="bg-[#0A0A0A] hover:bg-zinc-800"
            >
              {t("billing.switchTo")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
